/**
 * Runtime MFE loader for Module Federation remotes.
 *
 * Uses `@module-federation/runtime` via the RuntimeLoader from `@repo/mfe-core`
 * to register and load MFE remotes on demand. Static `import()` specifiers are
 * no longer needed — the RuntimeLoader handles dynamic registration and loading
 * at runtime based on the MFE Registry metadata.
 */
import { createRuntimeLoader } from "@repo/mfe-core/host";
import { getMfeByName } from "./helpers.js";

/** Singleton RuntimeLoader instance, created once per session. */
const runtimeLoader = createRuntimeLoader({
  useProdUrls: import.meta.env.PROD || import.meta.env.VITE_MFE_USE_PROD === "true",
  hostName: "ispm_host",
});

/**
 * Loads an MFE remote by name using the RuntimeLoader.
 *
 * Looks up the MFE definition from the registry, then delegates to the
 * RuntimeLoader which handles `registerRemotes()` + `loadRemote()` with
 * internal caching (subsequent calls for the same name return the cached module).
 *
 * @param {string} name — MFE name as registered in the registry (e.g. "integrations")
 * @returns {Promise<import("./types").MfeModule>}
 */
export function loadRemote(name) {
  const mfeDef = getMfeByName(name);
  if (!mfeDef) {
    return Promise.reject(
      new Error(`No MFE registered with name "${name}" in the registry`),
    );
  }
  return runtimeLoader.load(mfeDef);
}
